import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Link, useLocation } from 'react-router-dom'

import MiniHeader from '../../../../UI/MiniHeader'
import Search from '../../../../UI/Search'

import { TRoutes } from '../../../../shared/const'
import LaunchCard from '../Training/LaunchCard'

import { CHAPTERS } from './content'
import styles from './style.module.scss'

// Beschriftung der Hinweis-Box je nach ausgelöstem Kanal.
const COMM_HINT_LABEL = {
	email: 'E-Mail-Versand',
	internal: 'Interne Benachrichtigung',
	both: 'E-Mail & interne Benachrichtigung'
}

/**
 * Handbuch im Admin-Bereich.
 *
 * Beschreibt die Abläufe von Schüler:innen, Lehrkräften und Administration.
 * Die Inhalte sind aus dem Code abgeleitet — Statuswechsel, Pflichtfelder,
 * Kriterien. Wo sich etwas nicht belegen ließ, steht das ausdrücklich dabei
 * statt einer Vermutung.
 *
 * Bewusst als statischer Inhalt im Code und nicht in der Datenbank: Das
 * Handbuch beschreibt, wie die Anwendung funktioniert, und gehört damit zur
 * Anwendung. Eine Datenbankpflege würde es von der Software entkoppeln — bei
 * der nächsten Änderung stünde dort dann etwas Falsches.
 */
export default function Handbook() {
	const [activeId, setActiveId] = useState(CHAPTERS[0].id)
	const [search, setSearch] = useState('')
	// Abschnitt, zu dem gerade gesprungen wurde — dient zugleich als Hervorhebung,
	// damit erkennbar ist, wo man gelandet ist.
	const [jumpTarget, setJumpTarget] = useState(null)
	// Aktueller Treffer der Textsuche (Index) und Gesamtzahl. Die Zahl wird nach
	// dem Rendern aus den tatsächlich markierten Stellen ermittelt, damit sie mit
	// der Hervorhebung immer übereinstimmt.
	const [current, setCurrent] = useState(0)
	const [total, setTotal] = useState(0)

	const contentRef = useRef(null)
	const location = useLocation()

	// Tiefer Link aus dem Bereich „Kommunikation“: ?kapitel=<id> öffnet direkt
	// das passende Kapitel (und ?abschnitt=<id> springt zum Abschnitt).
	useEffect(() => {
		const params = new URLSearchParams(location.search)
		const kapitel = params.get('kapitel')
		if (kapitel && CHAPTERS.some(c => c.id === kapitel)) {
			setSearch('')
			setActiveId(kapitel)
			const abschnitt = params.get('abschnitt')
			if (abschnitt) setJumpTarget(abschnitt)
		}
	}, [location.search])

	const query = search.trim().toLowerCase()

	// Querverweis im Text: Kapitel wechseln, dann zum Abschnitt scrollen. Der
	// Wechsel und das Setzen des Ziels passieren im selben Rendering, der Effekt
	// findet das Element also bereits im DOM vor.
	const jumpTo = ({ chapter, section }) => {
		setSearch('')
		setActiveId(chapter)
		setJumpTarget(section)
	}

	useEffect(() => {
		if (!jumpTarget) return

		document.getElementById(jumpTarget)?.scrollIntoView({ behavior: 'smooth', block: 'start' })

		const timer = setTimeout(() => setJumpTarget(null), 2000)
		return () => clearTimeout(timer)
	}, [jumpTarget])

	// Hebt die Fundstellen des Suchbegriffs in einem Text hervor. Jede Markierung
	// bekommt `data-hb-mark`, damit sie sich nach dem Rendern in Dokumentreihen-
	// folge einsammeln lässt (Zählung und Sprungmarken). Enthält der Text den
	// Begriff nicht, bleibt er unverändert — keine überflüssigen Knoten.
	/*
	 * Hervorhebungen im Text: `**so**` wird fett.
	 *
	 * Die Auszeichnung steht seit jeher im Inhalt — an 73 Stellen quer durch
	 * alle Kapitel —, gerendert wurde sie nie. Auf dem Bildschirm standen die
	 * Sternchen mitten im Satz, und der hervorgehobene Teil war der einzige,
	 * der schlechter zu lesen war als der Rest.
	 *
	 * Kein Markdown-Paket: Gebraucht wird genau diese eine Auszeichnung. Alles
	 * Weitere (Überschriften, Listen, Verweise) hat das Handbuch als eigene
	 * Felder — `steps`, `table`, `note`, `warning`.
	 */
	const teileFett = text =>
		text
			.split(/(\*\*[^*]+\*\*)/g)
			.filter(Boolean)
			.map(teil =>
				teil.startsWith('**') && teil.endsWith('**') && teil.length > 4
					? { fett: true, text: teil.slice(2, -2) }
					: { fett: false, text: teil }
			)

	// Die Suche hebt innerhalb eines fetten Abschnitts weiter hervor: Beides
	// getrennt zu behandeln hieße, dass ein Treffer in einer Hervorhebung
	// unmarkiert bliebe — und genau dort steht oft das gesuchte Wort.
	const highlight = (text, keyPrefix) => {
		if (typeof text === 'string' && text.includes('**')) {
			return teileFett(text).map((teil, i) =>
				teil.fett ? (
					<b key={`${keyPrefix}-f${i}`}>{markiere(teil.text, `${keyPrefix}-f${i}`)}</b>
				) : (
					<span key={`${keyPrefix}-f${i}`}>{markiere(teil.text, `${keyPrefix}-f${i}`)}</span>
				)
			)
		}

		return markiere(text, keyPrefix)
	}

	const markiere = (text, keyPrefix) => {
		if (!query || typeof text !== 'string' || !text) return text

		const lower = text.toLowerCase()
		const out = []
		let from = 0
		let index
		let n = 0

		while ((index = lower.indexOf(query, from)) !== -1) {
			if (index > from) out.push(text.slice(from, index))
			out.push(
				<mark key={`${keyPrefix}-m${n++}`} data-hb-mark className={styles.mark}>
					{text.slice(index, index + query.length)}
				</mark>
			)
			from = index + query.length
		}

		if (!out.length) return text
		if (from < text.length) out.push(text.slice(from))
		return out
	}

	// Ein Schritt ist entweder reiner Text oder Text mit einem Querverweis, der
	// per {link} an seiner Stelle im Satz steht.
	const renderStep = (step, keyPrefix) => {
		if (typeof step === 'string') return highlight(step, keyPrefix)

		const [before, after] = step.text.split('{link}')

		return (
			<>
				{highlight(before, `${keyPrefix}-b`)}
				<button type="button" className={styles.link} onClick={() => jumpTo(step.link)}>
					{highlight(step.link.label, `${keyPrefix}-l`)}
				</button>
				{highlight(after, `${keyPrefix}-a`)}
			</>
		)
	}

	// Volltextsuche über alle Kapitel. Trifft ein Abschnitt, wird sein Kapitel
	// in der Navigation hervorgehoben.
	const matches = useMemo(() => {
		if (!query) return null

		return CHAPTERS.map(chapter => ({
			...chapter,
			sections: chapter.sections.filter(section => JSON.stringify(section).toLowerCase().includes(query))
		})).filter(chapter => chapter.sections.length)
	}, [query])

	const visible = matches || CHAPTERS.filter(chapter => chapter.id === activeId)

	// Bei jeder neuen Suche wieder beim ersten Treffer beginnen.
	useEffect(() => {
		setCurrent(0)
	}, [query])

	// Nur bei bewusstem Blättern (‹ / › bzw. Enter) zum Treffer scrollen — nicht
	// beim Tippen. Sonst rutscht die Seite bei jedem Zeichen weg und das Suchfeld
	// verschwindet, bevor man den Begriff zu Ende geschrieben hat. `navTick` löst
	// den Effekt auch dann aus, wenn der Index gleich bleibt (nur ein Treffer).
	const scrollToActive = useRef(false)
	const [navTick, setNavTick] = useState(0)

	// Nach dem Rendern: markierte Stellen einsammeln, ihre Zahl merken und die
	// aktuelle hervorheben. Angesprungen wird nur, wenn geblättert wurde.
	useLayoutEffect(() => {
		const nodes = contentRef.current ? Array.from(contentRef.current.querySelectorAll('[data-hb-mark]')) : []

		setTotal(nodes.length)
		if (!nodes.length) return

		const active = Math.min(current, nodes.length - 1)
		nodes.forEach((node, i) => node.classList.toggle(styles.markActive, i === active))

		if (scrollToActive.current) {
			nodes[active].scrollIntoView({ behavior: 'smooth', block: 'center' })
			scrollToActive.current = false
		}
	}, [query, current, activeId, navTick])

	const goToMatch = delta => {
		if (!total) return
		scrollToActive.current = true
		setNavTick(tick => tick + 1)
		setCurrent(prev => (prev + delta + total) % total)
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title="Handbuch" bg="#f8faff">
				<div className={styles.headerContent}>
					<Search
						value={search}
						placeholder="Im Handbuch suchen"
						onChange={e => setSearch(e.target.value)}
						// Enter springt zum nächsten Treffer, statt die Seite neu zu laden.
						onSubmit={e => {
							e.preventDefault()
							if (total) goToMatch(1)
						}}
					/>

					{query && (
						<div className={styles.matchBar}>
							{total > 0 ? (
								<>
									<span className={styles.matchCount}>
										{current + 1} / {total} Treffer
									</span>
									<button
										type="button"
										className={styles.matchNav}
										title="Vorheriger Treffer"
										aria-label="Vorheriger Treffer"
										onClick={() => goToMatch(-1)}
									>
										‹
									</button>
									<button
										type="button"
										className={styles.matchNav}
										title="Nächster Treffer"
										aria-label="Nächster Treffer"
										onClick={() => goToMatch(1)}
									>
										›
									</button>
								</>
							) : (
								<span className={styles.matchCount}>0 Treffer</span>
							)}
						</div>
					)}
				</div>
			</MiniHeader>

			<div className={styles.layout}>
				<nav className={styles.nav}>
					{CHAPTERS.map(chapter => {
						const hits = matches?.find(c => c.id === chapter.id)?.sections.length

						return (
							<button
								key={chapter.id}
								type="button"
								className={clsx(
									styles.navItem,
									!query && chapter.id === activeId && styles.navItemActive,
									query && !hits && styles.navItemMuted
								)}
								onClick={() => {
									setSearch('')
									setActiveId(chapter.id)
								}}
							>
								<span className={styles.navTitle}>{chapter.title}</span>
								<span className={styles.navSubtitle}>
									{query && hits ? `${hits} Treffer` : chapter.subtitle}
								</span>
							</button>
						)
					})}
				</nav>

				<div className={styles.content} ref={contentRef}>
					{/*
					 * Der Einstieg in die Schulung steht ganz oben und verschwindet
					 * bei einer Suche: Wer sucht, sucht etwas Bestimmtes und nicht
					 * ein Angebot.
					 */}
					{!query && <LaunchCard />}

					{query && !visible.length && <p className={styles.noHits}>Keine Treffer für „{search}“.</p>}

					{visible.map(chapter => (
						<article key={chapter.id} className={styles.chapter}>
							<h2 className={styles.chapterTitle}>{highlight(chapter.title, `${chapter.id}-title`)}</h2>
							{chapter.intro && (
								<p className={styles.intro}>{highlight(chapter.intro, `${chapter.id}-intro`)}</p>
							)}

							{chapter.sections.map((section, index) => (
								<section
									key={index}
									id={section.id}
									className={clsx(
										styles.section,
										section.id && section.id === jumpTarget && styles.sectionJumped
									)}
								>
									<h3 className={styles.sectionTitle}>
										{highlight(section.title, `${chapter.id}-${index}-h`)}
									</h3>

									{section.body?.map((paragraph, i) => (
										<p key={i} className={styles.paragraph}>
											{highlight(paragraph, `${chapter.id}-${index}-p${i}`)}
										</p>
									))}

									{section.steps && (
										<ol className={styles.steps}>
											{section.steps.map((step, i) => (
												<li key={i} className={styles.step}>
													<span className={styles.stepNumber}>{i + 1}</span>
													<span>{renderStep(step, `${chapter.id}-${index}-s${i}`)}</span>
												</li>
											))}
										</ol>
									)}

									{section.table && (
										<div className={styles.tableWrap}>
											<table className={styles.table}>
												<thead>
													<tr>
														{section.table.head.map((cell, i) => (
															<th key={cell}>
																{highlight(cell, `${chapter.id}-${index}-th${i}`)}
															</th>
														))}
													</tr>
												</thead>
												<tbody>
													{section.table.rows.map((row, i) => (
														<tr key={i}>
															{row.map((cell, j) => (
																<td key={j}>
																	{highlight(
																		cell,
																		`${chapter.id}-${index}-td${i}-${j}`
																	)}
																</td>
															))}
														</tr>
													))}
												</tbody>
											</table>
										</div>
									)}

									{section.note && (
										<p className={styles.note}>
											{highlight(section.note, `${chapter.id}-${index}-note`)}
										</p>
									)}
									{section.warning && (
										<p className={styles.warning}>
											{highlight(section.warning, `${chapter.id}-${index}-warn`)}
										</p>
									)}
									{section.commHint && (
										<div className={styles.commHint}>
											<span className={styles.commHintBadge}>
												{COMM_HINT_LABEL[section.commHint.channel]}
											</span>
											<span className={styles.commHintText}>
												{section.commHint.text}
												{/*
												 * Sind die ausgelösten Vorlagen benannt, führt der
												 * Verweis direkt zur einzelnen Vorlage statt nur in
												 * den Bereich — mit Betreff, damit schon hier
												 * erkennbar ist, was beim Empfänger ankommt.
												 */}
												{section.commHint.templates ? (
													<span className={styles.commHintTemplates}>
														{section.commHint.templates.map(template => (
															<Link
																key={`${template.channel}:${template.key}`}
																className={styles.commHintTemplate}
																to={`${TRoutes.COMMUNICATION}?tab=${template.channel}&item=${template.key}`}
															>
																<span className={styles.commHintTemplateTitle}>
																	{template.title}
																</span>
																{template.subject && (
																	<span className={styles.commHintTemplateSubject}>
																		Betreff: „{template.subject}“
																	</span>
																)}
															</Link>
														))}
													</span>
												) : (
													<>
														{' '}
														<Link className={styles.link} to={TRoutes.COMMUNICATION}>
															Vorlagen im Bereich „Kommunikation“ ansehen und bearbeiten
														</Link>
														.
													</>
												)}
											</span>
										</div>
									)}
									{section.docHint && (
										<div className={styles.docHint}>
											<span className={styles.docHintBadge}>Dokumentvorlage</span>
											<span className={styles.commHintText}>
												{section.docHint.text}{' '}
												<Link
													className={styles.link}
													to={`${TRoutes.SETTINGS}?item=${section.docHint.documentKey}#documentTemplates`}
												>
													Vorschau im Bereich „Einstellungen“ → „Dokumente“
												</Link>
												.
											</span>
										</div>
									)}
									{/*
									 * Verweis auf eine Einstellung. Der Sprungpunkt ist die
									 * Kennung des Abschnitts in „Einstellungen"; der Bereich
									 * wechselt dorthin von selbst (siehe Settings, `anchors`).
									 */}
									{section.settingLink && (
										<div className={styles.docHint}>
											<span className={styles.docHintBadge}>Einstellung</span>
											<span className={styles.commHintText}>
												{section.settingLink.text}{' '}
												<Link
													className={styles.link}
													to={`${TRoutes.SETTINGS}#${section.settingLink.anchor}`}
												>
													{section.settingLink.label}
												</Link>
												.
											</span>
										</div>
									)}
									{section.unverified && (
										<p className={styles.unverified}>
											Nicht aus dem Code belegbar:{' '}
											{highlight(section.unverified, `${chapter.id}-${index}-unv`)}
										</p>
									)}
								</section>
							))}
						</article>
					))}
				</div>
			</div>
		</div>
	)
}
